import { useMemo } from 'react';

import { isFeatureEnabled } from '@website/config/featureFlags';
import { extractCity } from '@website/lib/address';
import { Activity } from '@website/services/activities.service';
import { calculateClusterThreshold, calculateDistance, hasRealCoordinates } from '@website/utils/mapUtils';

interface UseFilteredActivitiesParams {
  activities?: Activity[];
  /**
   * Activities shown on the map but listed separately (the search "not bookable
   * yet" section). Their pins are muted unless a bookable activity shares them.
   */
  mapOnlyActivities?: Activity[];
  zoom?: number | null;
}

/** One bookable slot-location of an activity, as a map point. */
interface LocationPoint {
  activity: Activity;
  lat: number;
  lng: number;
}

/** A co-located group carrying more than one distinct activity (clustered pin). */
interface ClusteredMarker {
  id: string;
  latitude: number;
  longitude: number;
  name: string;
  count: number;
  activityIds: string[];
  /** Every activity at this pin is not bookable yet. */
  isMuted: boolean;
  activities: Array<{
    id: string;
    title: string;
    professionalName: string;
    startingPrice?: number | null;
    currency?: string;
    imageUrl?: string;
    isAtHome?: boolean;
    homeRadius?: number;
  }>;
}

/** A pin carrying a single distinct activity. */
interface SingleMarker {
  id: string;
  latitude: number;
  longitude: number;
  name: string;
  isAtHome?: boolean;
  homeRadius?: number;
  isMuted: boolean;
  /** Always absent on a single pin; declared so consumers can presence-narrow the
   *  union on `activityIds` (a clustered pin carries the real list). */
  activityIds?: undefined;
}

/**
 * One marker fed to GoogleMapWrapper. Structurally a subset of that component's
 * `Location` shape; consumers narrow on the clustered fields (`count` /
 * `activityIds`).
 */
type MarkerLocation = ClusteredMarker | SingleMarker;

/**
 * Build one map marker for a group of co-located points. The activity LIST is
 * de-duplicated to one row per activity (F10): with several pins per activity a
 * single activity can contribute more than one point, but it must appear at most
 * once in a marker's `activities` / `activityIds`. A marker carrying exactly one
 * distinct activity collapses to the single-activity shape (no count/list).
 */
function buildMarker(id: string, latitude: number, longitude: number, points: LocationPoint[]): MarkerLocation {
  const byId = new Map<string, Activity>();
  for (const p of points) {
    if (!byId.has(p.activity.id)) byId.set(p.activity.id, p.activity);
  }
  const distinct = Array.from(byId.values());
  const isMuted = distinct.every((a) => a.availability === 'none');

  if (distinct.length > 1) {
    return {
      id,
      latitude,
      longitude,
      name: `${distinct.length} activities`,
      count: distinct.length,
      activityIds: distinct.map((a) => a.id),
      isMuted,
      activities: distinct.map((a) => ({
        id: a.id,
        title: a.title,
        professionalName: a.professionalName,
        startingPrice: a.startingPrice,
        currency: 'CAD',
        imageUrl: a.imageUrl,
        isAtHome: a.isAtHome,
        homeRadius: a.homeRadius
      }))
    };
  }

  const activity = distinct[0];
  return {
    id: distinct.length === 1 ? activity.id : id,
    latitude,
    longitude,
    name: activity.title,
    isAtHome: activity.isAtHome,
    homeRadius: activity.homeRadius,
    isMuted
  };
}

export function useFilteredActivities({ activities, mapOnlyActivities, zoom }: UseFilteredActivitiesParams) {
  const allActivities = useMemo(() => activities || [], [activities]);
  const mapActivities = useMemo(
    () => (mapOnlyActivities?.length ? [...allActivities, ...mapOnlyActivities] : allActivities),
    [allActivities, mapOnlyActivities]
  );

  const ratingEnabled = isFeatureEnabled('RATING');

  const activitiesForListingCards = useMemo(() => {
    return allActivities.map((act: Activity) => ({
      id: act.id,
      title: act.title,
      imageUrl: act.imageUrl,
      professionalName: act.professionalName,
      isAtHome: act.isAtHome,
      locationKinds: act.locationKinds,
      startingPrice: act.startingPrice,
      bookingsSinceLastYear: act.bookingsSinceLastYear || 0,
      city: extractCity(act.location?.address),
      ratingAverage: ratingEnabled ? act.rating?.ratingAverage : undefined,
      ratingTotal: ratingEnabled ? act.rating?.ratingTotal : undefined
    }));
  }, [allActivities, ratingEnabled]);

  const atHomeActivities = useMemo(() => {
    return mapActivities
      .filter((activity) => activity.isAtHome)
      .map((a) => ({
        id: a.id,
        title: a.title,
        professionalName: a.professionalName,
        startingPrice: a.startingPrice,
        currency: 'CAD', // TODO: Get from activity if available
        imageUrl: a.imageUrl,
        isAtHome: a.isAtHome,
        homeRadius: a.homeRadius
      }));
  }, [mapActivities]);

  const mapLocations = useMemo(() => {
    // Slot-level-locations (Section 4 C2 / F10): an activity can now have several
    // DISTINCT bookable slot locations, so we expand each activity into one POINT
    // per distinct location (from `locationSet`, falling back to the single
    // activity coordinate). Markers are built per location point; the existing
    // zoom clustering is reused unchanged. Within a marker the activity LIST is
    // de-duplicated to one row per activity (no longer automatic with many pins).
    const points: LocationPoint[] = [];
    mapActivities.forEach((activity: Activity) => {
      if (activity.isAtHome) return; // at-home renders as a coverage area, not a pin
      const set = activity.locationSet && activity.locationSet.length > 0 ? activity.locationSet : undefined;
      if (set) {
        set.forEach((loc) => {
          if (loc.isAtHome) return;
          if (!Number.isFinite(loc.latitude) || !Number.isFinite(loc.longitude)) return;
          points.push({ activity, lat: loc.latitude, lng: loc.longitude });
        });
      } else if (activity.location?.coordinates) {
        const { latitude, longitude } = activity.location.coordinates;
        // A coordinate-less activity (online-only) defaults to 0/0 in the service
        // mapping - dropping a phantom pin in the Gulf of Guinea. No coords, no pin.
        if (!hasRealCoordinates(latitude, longitude)) return;
        points.push({ activity, lat: latitude, lng: longitude });
      }
    });

    const clusterThreshold = calculateClusterThreshold(zoom);
    const useExactMatching = clusterThreshold < 0.01;

    if (useExactMatching) {
      const locationGroups = new Map<string, { lat: number; lng: number; points: LocationPoint[] }>();

      points.forEach((point) => {
        const locationKey = `${point.lat},${point.lng}`;
        const group = locationGroups.get(locationKey);
        if (group) {
          group.points.push(point);
        } else {
          locationGroups.set(locationKey, { lat: point.lat, lng: point.lng, points: [point] });
        }
      });

      return Array.from(locationGroups.entries()).map(([locationKey, group]) =>
        buildMarker(locationKey, group.lat, group.lng, group.points)
      );
    }

    const clusters: Array<{ points: LocationPoint[]; centerLat: number; centerLng: number }> = [];
    const processed = new Set<number>();

    points.forEach((point, index) => {
      if (processed.has(index)) return;

      const nearby: number[] = [index];
      processed.add(index);

      let changed = true;
      while (changed) {
        changed = false;

        const currentCenterLat = nearby.reduce((sum, i) => sum + points[i].lat, 0) / nearby.length;
        const currentCenterLng = nearby.reduce((sum, i) => sum + points[i].lng, 0) / nearby.length;

        points.forEach((other, otherIndex) => {
          if (processed.has(otherIndex)) return;
          const distance = calculateDistance(currentCenterLat, currentCenterLng, other.lat, other.lng);
          if (distance <= clusterThreshold) {
            nearby.push(otherIndex);
            processed.add(otherIndex);
            changed = true;
          }
        });
      }

      const centerLat = nearby.reduce((sum, i) => sum + points[i].lat, 0) / nearby.length;
      const centerLng = nearby.reduce((sum, i) => sum + points[i].lng, 0) / nearby.length;
      clusters.push({ points: nearby.map((i) => points[i]), centerLat, centerLng });
    });

    return clusters.map((cluster) =>
      buildMarker(
        `cluster_${cluster.centerLat.toFixed(6)}_${cluster.centerLng.toFixed(6)}`,
        cluster.centerLat,
        cluster.centerLng,
        cluster.points
      )
    );
  }, [mapActivities, zoom]);

  return {
    mapActivities,
    activitiesForListingCards,
    mapLocations,
    atHomeActivities
  };
}
